/**
 * KontaktDetailPage — Kontakt-Detail mit Sub-Navigation (7 Tabs)
 * Route: /kontakte/:id/:section?
 * Pattern: analog zu FahrzeugDetail — URL-basierte Tab-Navigation mit Sticky-Header
 *
 * Issue #574 — Kontakte Stufe 2: Sub-Navi + Verknüpfungs-Tab
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  User, Link2, MessageCircle, Euro, Clock, StickyNote, FileText,
  ChevronRight, Home, Loader2, Phone, Mail, Plus,
  Briefcase, Car, Users, Building2,
} from 'lucide-react';
import {
  useCRMKontaktBeteiligungen,
  type CRMKontakt,
  type CRMKontaktBeteiligung,
} from '@/hooks/useCRM';
import { STATUS_LABELS, STATUS_COLORS } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import { LABEL_CONFIG, type KontaktLabel } from '@/lib/kontakt-labels';
import { MEINE_FIRMEN } from '@/lib/meine-firmen';

const KOOP_RED = '#C41E3A';

// ─── Types ────────────────────────────────────────────────────────────────────

type SectionId = 'uebersicht' | 'verknuepfungen' | 'kommunikation' | 'finanzen' | 'historie' | 'notizen' | 'dokumente';

const TABS: { id: SectionId; label: string; icon: typeof User }[] = [
  { id: 'uebersicht',    label: 'Übersicht',     icon: User },
  { id: 'verknuepfungen',label: 'Verknüpfungen', icon: Link2 },
  { id: 'kommunikation', label: 'Kommunikation', icon: MessageCircle },
  { id: 'finanzen',      label: 'Finanzen',       icon: Euro },
  { id: 'historie',      label: 'Historie',       icon: Clock },
  { id: 'notizen',       label: 'Notizen',        icon: StickyNote },
  { id: 'dokumente',     label: 'Dokumente',      icon: FileText },
];

const IMPLEMENTED_SECTIONS: SectionId[] = ['uebersicht', 'verknuepfungen'];


const BUSINESS_UNIT_TABS: { id: string; label: string }[] = [
  { id: 'gutachten', label: 'Gutachten' },
  { id: 'autoglas',  label: 'Autoglas' },
  { id: 'mietwagen', label: 'Mietwagen' },
  { id: 'werkstatt', label: 'Werkstatt' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── Übersicht-Tab ────────────────────────────────────────────────────────────

function UebersichtTab({ kontakt, labels, onAssignFirma }: { kontakt: CRMKontakt; labels: string[]; onAssignFirma: (value: string) => void }) {
  const firmaZuordnung = (kontakt as any).firma_zuordnung as string | null | undefined;
  const stammdaten: [string, string][] = [
    ['E-Mail', kontakt.email || ''],
    ['Telefon', kontakt.telefon || ''],
    ['Mobil', kontakt.mobil || ''],
    ['Firma', kontakt.firma || ''],
    ['Position', kontakt.position || ''],
    ['Adresse', kontakt.adresse || ''],
    ['PLZ / Ort', [kontakt.plz, kontakt.ort].filter(Boolean).join(' ') || ''],
    ['Webseite', kontakt.webseite || ''],
    ['Status', kontakt.status || 'aktiv'],
    ['Angelegt am', formatDate(kontakt.erstellt_am)],
  ].filter(([, v]) => v) as [string, string][];

  return (
    <div className="space-y-4">
      {/* Meine Firma — Zuordnung zu eigenem Betrieb */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Meine Firma</h3>
        <select
          value={firmaZuordnung || ''}
          onChange={(e) => onAssignFirma(e.target.value)}
          data-testid="kontakt-firma-zuordnung"
          className="w-full sm:w-64 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/20"
        >
          <option value="">— Keiner Firma zugeordnet —</option>
          {MEINE_FIRMEN.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>
      {/* Labels */}
      {labels.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Labels</h3>
          <div className="flex flex-wrap gap-1.5">
            {labels.map(label => {
              const cfg = LABEL_CONFIG[label as KontaktLabel];
              if (!cfg) return null;
              return (
                <span
                  key={label}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{ backgroundColor: cfg.bg, color: cfg.color }}
                >
                  {cfg.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Stammdaten */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Stammdaten</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {stammdaten.map(([key, value]) => (
            <div key={key}>
              <dt className="text-[11px] text-gray-400 font-medium">{key}</dt>
              <dd className="text-[13px] text-gray-800 font-medium mt-0.5 break-words">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Tags (legacy) */}
      {kontakt.tags && kontakt.tags.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Kategorien</h3>
          <div className="flex flex-wrap gap-1.5">
            {kontakt.tags.map(tag => (
              <span key={tag} className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-[11px] font-medium">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Verknüpfungs-Tab ─────────────────────────────────────────────────────────

function VerknuepfungenTab({
  kontaktId,
  beteiligungen,
  loading,
}: {
  kontaktId: string;
  beteiligungen: CRMKontaktBeteiligung[];
  loading: boolean;
}) {
  const [, setLocation] = useLocation();
  const [activeUnit, setActiveUnit] = useState<string>('gutachten');

  const aktenByUnit = BUSINESS_UNIT_TABS.reduce<Record<string, CRMKontaktBeteiligung[]>>((acc, unit) => {
    acc[unit.id] = beteiligungen.filter(b => b.business_unit === unit.id);
    return acc;
  }, {});

  const aktiveAkten = aktenByUnit[activeUnit] ?? [];

  return (
    <div data-testid="verknuepfungen-tab" className="space-y-5">
      {/* Akten */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="px-4 pt-4 pb-0">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-bold text-gray-800">Akten</h3>
          </div>

          {/* Business-Unit-Tabs */}
          <nav data-testid="verknuepfungen-akten-tabs" className="flex gap-0 border-b border-gray-100 -mx-4 px-4 overflow-x-auto scrollbar-none">
            {BUSINESS_UNIT_TABS.map(unit => {
              const count = aktenByUnit[unit.id]?.length ?? 0;
              const isActive = unit.id === activeUnit;
              return (
                <button
                  key={unit.id}
                  onClick={() => setActiveUnit(unit.id)}
                  data-active={isActive}
                  className={`flex-shrink-0 px-3 py-2 text-[12px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-[#C41E3A] text-[#C41E3A]'
                      : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {unit.label}
                  {count > 0 && (
                    <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-red-50 text-[#C41E3A]' : 'bg-gray-100 text-gray-500'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Akten-Liste */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
            </div>
          ) : aktiveAkten.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="w-6 h-6 text-gray-200 mx-auto mb-2" />
              <p className="text-[12px] text-gray-400">Keine {BUSINESS_UNIT_TABS.find(u => u.id === activeUnit)?.label}-Akten</p>
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-gray-400 text-[10px] uppercase tracking-widest">
                  <th className="text-left pb-2 font-semibold">Akten-Nr.</th>
                  <th className="text-left pb-2 font-semibold hidden sm:table-cell">Titel</th>
                  <th className="text-left pb-2 font-semibold hidden sm:table-cell">Rolle</th>
                  <th className="text-left pb-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {aktiveAkten.map((b, idx) => {
                  const identifier = aktenIdentifier({ akten_nummer: b.akten_nummer, akten_nr: b.akten_nr });
                  const href = identifier ? buildAkteHref(b.business_unit, identifier, 'uebersicht') : null;
                  const statusLabel = STATUS_LABELS[b.status as keyof typeof STATUS_LABELS] ?? b.status ?? '—';
                  const statusClass = STATUS_COLORS[b.status as keyof typeof STATUS_COLORS] ?? 'bg-gray-100 text-gray-600';
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => href && setLocation(href)}
                    >
                      <td className="py-2.5 pr-3 font-mono font-bold text-gray-800 whitespace-nowrap">
                        {identifier ?? '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-600 truncate max-w-[180px] hidden sm:table-cell">
                        {b.titel ?? '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-500 hidden sm:table-cell capitalize">
                        {b.rolle ?? '—'}
                      </td>
                      <td className="py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Dokumente Platzhalter */}
      <VerknuepfungenSection icon={FileText} title="Dokumente" emptyText="Keine Dokumente verknüpft" />

      {/* Fahrzeuge Platzhalter */}
      <VerknuepfungenSection icon={Car} title="Fahrzeuge" emptyText="Keine Fahrzeuge verknüpft" />

      {/* Mitarbeiter-Zuständigkeit Platzhalter */}
      <VerknuepfungenSection icon={Users} title="Mitarbeiter-Zuständigkeit" emptyText="Noch keine Zuordnung" />

      {/* Rechnungen Platzhalter */}
      <VerknuepfungenSection icon={Building2} title="Rechnungen & Forderungen" emptyText="Keine Rechnungen vorhanden" />
    </div>
  );
}

function VerknuepfungenSection({
  icon: Icon,
  title,
  emptyText,
}: {
  icon: typeof FileText;
  title: string;
  emptyText: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-400" />
        <h3 className="text-[13px] font-bold text-gray-800">{title}</h3>
      </div>
      <div className="text-center py-5">
        <Icon className="w-5 h-5 text-gray-200 mx-auto mb-1.5" />
        <p className="text-[12px] text-gray-400">{emptyText}</p>
      </div>
    </div>
  );
}

// ─── Platzhalter für nicht implementierte Tabs ────────────────────────────────

function PlatzhalterTab({ label }: { label: string }) {
  return (
    <div
      data-testid="tab-in-entwicklung"
      className="flex flex-col items-center justify-center py-16 text-center"
    >
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <FileText className="w-5 h-5 text-gray-300" />
      </div>
      <h3 className="text-sm font-semibold text-gray-500">{label}</h3>
      <p className="text-xs text-gray-400 mt-1">Dieser Bereich ist in Entwicklung.</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function KontaktDetailPage() {
  const params = useParams<{ id: string; section?: string }>();
  const kontaktId = params.id;
  const section = (params.section || 'uebersicht') as SectionId;
  const [, setLocation] = useLocation();

  const [kontakt, setKontakt] = useState<CRMKontakt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [labels, setLabels] = useState<string[]>([]);

  const { beteiligungen, loading: betLoading } = useCRMKontaktBeteiligungen(kontaktId);

  // Redirect /kontakte/:id → /kontakte/:id/uebersicht
  useEffect(() => {
    if (!params.section && kontaktId) {
      setLocation(`/kontakte/${kontaktId}/uebersicht`, { replace: true });
    }
  }, [params.section, kontaktId, setLocation]);

  async function assignFirma(value: string) {
    if (!kontaktId) return;
    const { error: updErr } = await supabase
      .from('crm_kontakte')
      .update({ firma_zuordnung: value || null })
      .eq('id', kontaktId);
    if (updErr) { toast.error('Firma-Zuordnung konnte nicht gespeichert werden'); return; }
    toast.success('Firma-Zuordnung gespeichert');
    setKontakt((k) => (k ? ({ ...k, firma_zuordnung: value || null } as CRMKontakt) : k));
  }

  const fetchKontakt = useCallback(async () => {
    if (!kontaktId) return;
    setLoading(true);
    const { data, error: err } = await supabase
      .from('crm_kontakte')
      .select('*')
      .eq('id', kontaktId)
      .single();
    if (err) {
      setError(err.message);
      toast.error('Kontakt konnte nicht geladen werden');
    } else {
      setKontakt(data as CRMKontakt);
    }
    setLoading(false);
  }, [kontaktId]);

  const fetchLabels = useCallback(async () => {
    if (!kontaktId) return;
    try {
      const res = await fetch('/api/kontakt-labels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getLabels', kontakt_id: kontaktId }),
      });
      if (res.ok) {
        const json = await res.json();
        setLabels(Array.isArray(json.labels) ? json.labels : []);
      }
    } catch {
      // Labels are optional — don't toast on failure
    }
  }, [kontaktId]);

  useEffect(() => {
    fetchKontakt();
    fetchLabels();
  }, [fetchKontakt, fetchLabels]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Kontakt wird geladen...</span>
      </div>
    );
  }

  if (error || !kontakt) {
    return (
      <div className="p-8 text-center min-h-[200px] flex flex-col items-center justify-center">
        <User className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-500">Kontakt nicht gefunden</p>
        <button
          onClick={() => setLocation('/kontakte')}
          className="mt-3 text-sm hover:underline font-medium"
          style={{ color: KOOP_RED }}
        >
          Zurück zur Kontaktliste
        </button>
      </div>
    );
  }

  const displayName = kontakt.name?.replace(/^"+|"+$/g, '').trim() || 'Unbenannt';
  const initials = getInitials(displayName);
  const activeTab = TABS.find(t => t.id === section) ?? TABS[0];

  // Merge tags and labels for display — show new labels preferentially
  const displayLabels = labels.length > 0 ? labels : (kontakt.tags ?? []);

  return (
    <div className="flex flex-col h-full">
      {/* ── Breadcrumb-Leiste ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-5 py-2 flex items-center gap-2 flex-shrink-0 flex-wrap">
        <nav className="flex items-center gap-1 text-[11px] sm:text-[12px] text-gray-400 flex-shrink-0">
          <Link href="/" className="hover:text-gray-600 transition-colors">
            <Home className="w-3.5 h-3.5" />
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link href="/kontakte" className="hover:text-gray-600 transition-colors">Kontakte</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-700 font-medium truncate">{displayName}</span>
        </nav>
      </div>

      {/* ── Scrollbarer Inhalt ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">

        {/* ── Sticky Header + Sub-Nav ──────────────────────────────────────── */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200" data-testid="kontakt-detail-header">

          {/* Kontakt-Header */}
          <div className="px-3 sm:px-4 lg:px-5 py-3">
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
                style={{ backgroundColor: '#1B3A5C' }}
              >
                {initials}
              </div>

              {/* Name + Labels + Firma */}
              <div className="flex-1 min-w-0">
                <h1 className="text-sm sm:text-base font-bold text-gray-900 truncate">{displayName}</h1>
                {kontakt.firma && (
                  <p className="text-[11px] text-gray-500 truncate">{kontakt.firma}</p>
                )}
                {/* Labels as Pills */}
                {displayLabels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {displayLabels.slice(0, 4).map(label => {
                      const cfg = LABEL_CONFIG[label as KontaktLabel];
                      if (cfg) {
                        return (
                          <span
                            key={label}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ backgroundColor: cfg.bg, color: cfg.color }}
                          >
                            {cfg.label}
                          </span>
                        );
                      }
                      return (
                        <span key={label} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                          {label}
                        </span>
                      );
                    })}
                    {displayLabels.length > 4 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-400">
                        +{displayLabels.length - 4}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Quick-Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {kontakt.telefon && (
                  <a
                    data-testid="quick-action-anrufen"
                    href={`tel:${kontakt.telefon.replace(/\s/g, '')}`}
                    title="Anrufen"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </a>
                )}
                {!kontakt.telefon && (
                  <span
                    data-testid="quick-action-anrufen"
                    title="Kein Telefon hinterlegt"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 border border-gray-100"
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </span>
                )}

                {kontakt.email && (
                  <a
                    data-testid="quick-action-mailen"
                    href={`mailto:${kontakt.email}`}
                    title="E-Mail senden"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </a>
                )}
                {!kontakt.email && (
                  <span
                    data-testid="quick-action-mailen"
                    title="Keine E-Mail hinterlegt"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 border border-gray-100"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </span>
                )}

                <Link
                  data-testid="quick-action-akte-erstellen"
                  href={`/akten/gutachten?kontakt_id=${kontaktId}&kontakt_name=${encodeURIComponent(displayName)}`}
                  title="Akte erstellen"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  <Plus className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Sub-Navigation */}
          <nav
            data-testid="kontakt-subnav"
            className="flex items-center gap-0 px-3 sm:px-4 lg:px-5 overflow-x-auto scrollbar-none"
          >
            {TABS.map(tab => {
              const isActive = tab.id === section;
              return (
                <button
                  key={tab.id}
                  data-active={isActive}
                  onClick={() => setLocation(`/kontakte/${kontaktId}/${tab.id}`)}
                  className={`flex-shrink-0 px-3 py-2.5 text-[12px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-[#C41E3A] text-[#C41E3A]'
                      : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* ── Tab-Inhalt ────────────────────────────────────────────────────── */}
        <div className="p-3 sm:p-4 lg:p-5">
          {section === 'uebersicht' ? (
            <UebersichtTab kontakt={kontakt} labels={labels} onAssignFirma={assignFirma} />
          ) : section === 'verknuepfungen' ? (
            <VerknuepfungenTab
              kontaktId={kontaktId}
              beteiligungen={beteiligungen}
              loading={betLoading}
            />
          ) : (
            <PlatzhalterTab label={activeTab.label} />
          )}
        </div>
      </div>
    </div>
  );
}
